import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorText } from "@/lib/api";
import { txt } from "@/lib/format";

export default function DeleteCategoryDialog({ category, categories, onClose, onDone, t, lang }) {
  const others = categories.filter((c) => c.id !== category.id);
  const [mode, setMode] = useState(others.length ? "move" : "delete_items");
  const [target, setTarget] = useState(others[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const has = category.item_count > 0;

  const run = async () => {
    setBusy(true);
    try {
      const params = has ? { mode, ...(mode === "move" ? { target } : {}) } : {};
      await api.delete(`/admin/categories/${category.id}`, { params });
      toast.success(t("toast.deleted"));
      onDone();
    } catch (e) {
      toast.error(errorText(e, t("toast.saveFailed")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="delete-category-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{t("cat.deleteTitle", { name: txt(category, "name", lang) })}</AlertDialogTitle>
          <AlertDialogDescription>{has ? t("cat.deleteHas", { n: category.item_count }) : t("cat.deleteEmpty")}</AlertDialogDescription>
        </AlertDialogHeader>
        {has && (
          <RadioGroup value={mode} onValueChange={setMode} className="space-y-3">
            {others.length > 0 && (
              <div className="space-y-2 rounded-xl border p-3">
                <label className="flex items-center gap-2 text-sm font-medium"><RadioGroupItem value="move" data-testid="delete-mode-move" />{t("cat.moveTo")}</label>
                {mode === "move" && (
                  <Select value={target} onValueChange={setTarget}>
                    <SelectTrigger data-testid="delete-target-select"><SelectValue placeholder={t("cat.target")} /></SelectTrigger>
                    <SelectContent>{others.map((c) => <SelectItem key={c.id} value={c.id}>{txt(c, "name", lang)}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
            )}
            <label className="flex items-center gap-2 rounded-xl border border-destructive/40 p-3 text-sm font-medium text-red-300"><RadioGroupItem value="delete_items" data-testid="delete-mode-delete-items" />{t("cat.deleteAll", { n: category.item_count })}</label>
          </RadioGroup>
        )}
        <p className="text-xs text-muted-foreground">{t("cat.hideTip")}</p>
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel data-testid="delete-category-cancel">{t("common.cancel")}</AlertDialogCancel>
          <Button variant="destructive" onClick={run} disabled={busy || (has && mode === "move" && !target)} data-testid="delete-category-confirm">{t("common.delete")}</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
